import { ColumnDef, Row } from "@tanstack/react-table";
import { useMemo } from "react";
import { CSVLink } from "react-csv";
import { Icon } from "../../primitives";

type CSVDownloadProps<TData extends object> = {
	name: string;
	columns: ColumnDef<TData, unknown>[];
	rows: Row<TData>[];
};

export const CSVDownload = <TData extends object>({
	name,
	columns,
	rows,
}: CSVDownloadProps<TData>) => {
	const { csvData, csvHeaders } = useMemo(() => {
		const headers = columns.flatMap((col) => {
			const key =
				col.meta?.csvKey ??
				("accessorKey" in col && typeof col.accessorKey === "string"
					? col.accessorKey
					: undefined) ??
				col.id;
			const label = col.meta?.csvLabel ?? (typeof col.header === "string" ? col.header : undefined);
			if (!key || !label || !col.id) return [];
			return [{ key, label, columnId: col.id, csvFormatter: col.meta?.csvFormatter }];
		});

		const csvRows = rows.map((row) => {
			const obj: Record<string, unknown> = {};
			for (const h of headers) {
				const raw = row.getValue(h.columnId);
				obj[h.key] = h.csvFormatter ? h.csvFormatter(raw) : raw;
			}
			return obj;
		});

		return {
			csvData: csvRows,
			csvHeaders: headers.map(({ key, label }) => ({ key, label })),
		};
	}, [rows, columns]);
	return (
		<div className="absolute top-[-40px] right-0">
			<div className="flex justify-end items-center pb-2">
				<span
					title="Export to CSV"
					className="p-1 rounded-md hover:bg-surface-neutral cursor-pointer"
				>
					<CSVLink filename={`data-table-${name}-export.csv`} data={csvData} headers={csvHeaders}>
						<Icon type="document-arrow-down" size="small" color="secondary" />
					</CSVLink>
				</span>
			</div>
		</div>
	);
};
